/**
 * runLogModel — merges a run's chronology with its declared step manifest into
 * the ONE ordered list the Steps log renders, in ora and in the plugin.
 *
 * THE ORDER IS THE DECLARED MANIFEST'S — the sequence the workflow is expected
 * to run in. This is a deliberate reversal of the log's original rule ("order
 * is the server's, never walk the manifest"), made after the operator saw a
 * terminal run whose seven never-run steps were all dumped at the end of the
 * list: two Epic login-spine steps that belong at position ~5, a branch gate
 * that belongs at ~13, and an eDoc wait that belongs at ~43. Appending them
 * put every one of them in a place the workflow would never have run them, so
 * the list stopped describing the workflow at all.
 *
 * What the reversal costs, and why it is affordable here: declaration order
 * diverges from what happened the moment a run loops. It cannot diverge today
 * — `workflow_instance_phase_history` is keyed PRIMARY KEY (instance_id,
 * phase_id) with no attempt/sequence column, so a phase physically cannot
 * appear twice for one instance and a re-entered phase updates its existing
 * row instead of adding one. `insertChronologically` below is nevertheless
 * written to place any row the manifest does not account for at its true
 * position in time, so a future schema that adds a sequence column gets loop
 * ordering without another redesign.
 *
 * What it never does: invent a state. Every "ran" state is derived from
 * timestamps the server actually reported, and the three never-ran states are
 * distinguished only by position relative to the furthest point the run
 * demonstrably reached (`highWater`) — not by guessing intent.
 */

import type { ChronologyRow } from "../../phaseModel";

/**
 * Ran states come from timestamps; never-ran states come from position.
 *
 *   completed   — the server reported an end time.
 *   running     — in flight: no end time, and it is the live current step.
 *   failed      — no end time, and the run ended in a failure-like status.
 *   unfinished  — it started, never reported an end, and the run moved past
 *                 it. Honest "not confirmed complete", not a claim of failure.
 *   skipped     — declared, never ran, and the run went PAST its position.
 *                 A branch not taken.
 *   not_reached — declared, never ran, and the run ended before reaching it.
 *   upcoming    — declared, never ran, still ahead of a live run.
 */
export type RunLogRowState =
  | "completed"
  | "running"
  | "failed"
  | "unfinished"
  | "skipped"
  | "not_reached"
  | "upcoming";

/** True for the states that mean "this never executed". */
export const NEVER_RAN_STATES: ReadonlySet<RunLogRowState> = new Set<RunLogRowState>([
  "skipped",
  "not_reached",
  "upcoming",
]);

export const RUN_LOG_STATE_EXPLANATIONS: Record<RunLogRowState, string> = {
  completed: "Finished successfully",
  running: "In progress",
  failed: "Failed",
  unfinished: "Not confirmed complete",
  skipped: "Skipped — the run went past this step",
  not_reached: "Not reached — the run ended first",
  upcoming: "Not yet started",
};

/** One entry of the workflow's own declared step manifest, in declared order. */
export interface RunLogStep {
  id: string;
  label: string;
  /** RAW catalog kind ("rpa"/"hitl"/"python"/…) — never the normalized
   *  domain-badge category, which `formatKind` does not recognise. */
  kind?: string | null;
}

export interface RunLogRow {
  /** Stable React key. */
  key: string;
  stepId: string | null;
  label: string;
  state: RunLogRowState;
  startedAt: string | null;
  endedAt: string | null;
  phaseLabel: string | null;
  milestoneLabel: string | null;
  /** Belongs to no phase (the Epic login spine). */
  exempt: boolean;
  kind: string | null;
  /** 1-based position in the rendered list. */
  position: number;
}

export interface BuildRunLogInput {
  /** `null` = the server reports no chronology at all; `[]` = none yet. */
  chronology: ChronologyRow[] | null;
  /** The workflow's declared steps, in declared order. Empty when the catalog
   *  entry is unavailable — the log then degrades to chronology-only, which is
   *  exactly the pre-manifest behaviour. */
  manifest: RunLogStep[];
  /** The substrate's live current-phase pointer. The substrate can advance it
   *  before a chronology row exists for that phase, so a step with no
   *  chronology entry can still be the one running right now. */
  currentStepId?: string | null;
  isTerminal?: boolean;
  isFailed?: boolean;
}

/** Insert `row` into `list` after the last entry whose anchor time is at or
 *  before its own — i.e. at its true position in time. Rows with no start time
 *  cannot be placed by time and go to the end of the run of equal anchors. */
function insertChronologically(
  list: { row: RunLogRow; anchor: string | null }[],
  entry: { row: RunLogRow; anchor: string | null },
): void {
  if (!entry.anchor) {
    list.push(entry);
    return;
  }
  const at = list.findIndex(
    (existing) => existing.anchor != null && existing.anchor > entry.anchor!,
  );
  if (at === -1) list.push(entry);
  else list.splice(at, 0, entry);
}

/**
 * Build the ordered Steps list.
 *
 * Returns `null` only when the server reports no chronology at all AND the
 * workflow declares no steps either — the caller renders "this run does not
 * report a history", which is a different statement from "nothing has happened
 * yet" (an empty list).
 *
 * A null chronology WITH a declared manifest is a third case and is not an
 * absence: the workflow's declared steps are real information, known before
 * the run reports anything, and they are what the retired drawer body showed
 * from the catalog alone. Returning null there made a run that had not yet
 * reported a chronology — a run that has just started, or an older instance —
 * claim to have no history when its steps were sitting in hand. Those rows
 * come back as `upcoming`, which is what they are.
 */
export function buildRunLog({
  chronology,
  manifest,
  currentStepId = null,
  isTerminal = false,
  isFailed = false,
}: BuildRunLogInput): RunLogRow[] | null {
  if (chronology == null && manifest.length === 0) return null;
  // Treated as "reported nothing yet" from here on; the declared manifest
  // below still contributes its own rows.
  chronology = chronology ?? [];

  // First occurrence per step id claims that step's place on the spine. Any
  // further occurrence is a loop and gets placed by time in the second pass.
  // (Unreachable today — see the module docstring.)
  const firstById = new Map<string, ChronologyRow>();
  for (const row of chronology) {
    const id = row.stepId;
    if (id && !firstById.has(id)) firstById.set(id, row);
  }

  const declaredIndex = new Map<string, number>();
  const kindById = new Map<string, string | null>();
  manifest.forEach((step, i) => {
    declaredIndex.set(step.id, i);
    kindById.set(step.id, step.kind ?? null);
  });

  // The furthest declared position the run demonstrably reached. Everything
  // never-run before it was passed over; everything after it was not reached.
  let highWater = -1;
  for (const id of firstById.keys()) {
    const i = declaredIndex.get(id);
    if (i != null && i > highWater) highWater = i;
  }

  // The last step to have started is the fallback "current" when the substrate
  // pointer is absent — only meaningful while the run is live.
  let latestStartedId: string | null = null;
  let latestStartedAt: string | null = null;
  for (const [id, row] of firstById) {
    if (row.startedAt && (!latestStartedAt || row.startedAt > latestStartedAt)) {
      latestStartedAt = row.startedAt;
      latestStartedId = id;
    }
  }

  const stateForRan = (id: string | null, row: ChronologyRow): RunLogRowState => {
    if (row.endedAt) return "completed";
    // No end time. Only the step the run is actually ON can be called running
    // or failed; any other unfinished row is just that — unfinished.
    const isCurrent = id != null && (id === currentStepId || id === latestStartedId);
    if (!isCurrent) return "unfinished";
    if (isFailed) return "failed";
    if (!isTerminal) return "running";
    return "unfinished";
  };

  const stateForNeverRan = (index: number): RunLogRowState => {
    if (index < highWater) return "skipped";
    if (isTerminal) return "not_reached";
    return "upcoming";
  };

  // ── the spine: every declared step, in declared order ────────────────────
  const placed: { row: RunLogRow; anchor: string | null }[] = [];
  let carriedAnchor: string | null = null;
  for (let i = 0; i < manifest.length; i += 1) {
    const step = manifest[i];
    const ran = firstById.get(step.id);
    if (ran?.startedAt) carriedAnchor = ran.startedAt;
    // A step with no chronology row can still be the live one: the substrate
    // advances current_phase before the phase-history row lands.
    const state = ran
      ? stateForRan(step.id, ran)
      : step.id === currentStepId && !isTerminal
        ? "running"
        : stateForNeverRan(i);
    placed.push({
      // Un-run steps inherit the previous ran step's time so they sit with it
      // rather than attracting rows that happened much later.
      anchor: ran?.startedAt ?? carriedAnchor,
      row: {
        key: `step-${step.id}`,
        stepId: step.id,
        label: ran?.stepLabel ?? step.label,
        state,
        startedAt: ran?.startedAt ?? null,
        endedAt: ran?.endedAt ?? null,
        phaseLabel: ran?.phaseLabel ?? null,
        milestoneLabel: ran?.milestoneLabel ?? null,
        exempt: ran?.exempt ?? false,
        kind: step.kind ?? null,
        position: 0,
      },
    });
  }

  // ── everything the spine did not account for ───────────────────────
  // Chronology rows the manifest does not declare, plus any repeat. Walked in
  // the SERVER's order, so that when there is no manifest at all — every row
  // undeclared — the result is the server's order exactly, which is the
  // pre-manifest behaviour this must degrade to.
  //
  // A row with a start time is placed by time, landing between the declared
  // steps it actually ran between. A row WITHOUT one cannot be placed by time
  // (the contract allows a null started_at), so it goes directly after
  // whichever row preceded it in the chronology — keeping it beside its own
  // neighbour instead of sinking to the end of the list.
  const spineRow = new Map<string, ChronologyRow>();
  for (const step of manifest) {
    const ran = firstById.get(step.id);
    if (ran) spineRow.set(step.id, ran);
  }
  let floatSeq = 0;
  let lastPlacedAt = -1;
  for (const row of chronology) {
    const id = row.stepId;
    if (id != null && spineRow.get(id) === row) {
      lastPlacedAt = placed.findIndex((p) => p.row.stepId === id);
      continue;
    }
    const entry = {
      anchor: row.startedAt,
      row: {
        key: `extra-${id ?? "entry"}-${(floatSeq += 1)}`,
        stepId: id,
        label: row.stepLabel ?? id ?? "Step not reported",
        state: stateForRan(id, row),
        startedAt: row.startedAt,
        endedAt: row.endedAt,
        phaseLabel: row.phaseLabel,
        milestoneLabel: row.milestoneLabel,
        exempt: row.exempt,
        // A repeat of a DECLARED step keeps the manifest's kind; an undeclared
        // one has none to borrow and reports none rather than guessing.
        kind: id ? (kindById.get(id) ?? null) : null,
        position: 0,
      },
    };
    if (row.startedAt) {
      insertChronologically(placed, entry);
      lastPlacedAt = placed.indexOf(entry);
    } else {
      lastPlacedAt = lastPlacedAt + 1;
      placed.splice(lastPlacedAt, 0, entry);
    }
  }

  return placed.map((entry, i) => ({ ...entry.row, position: i + 1 }));
}
